import { LockFilled } from '@ant-design/icons'
import { Alert } from 'antd'
import ResetPasswordLink from '@User/LoginPage/ResetPasswordLink'
import {
    LoginErrorResponseType,
    LoginErrorType,
    LoginErrorEnum,
} from '@User/LoginPage/types'

import Link from '~/core/Link'
import { HELP_EMAIL } from '~/lib/emailAndUrls'
import { mailtoLink } from '~/lib/mailtoLink'

import { LoginAlertDataType, LoginAlertMessageType } from './types'

const PLACEMENT = 'login-page-error-alert'

const incorrectEmailMessage = (): LoginAlertDataType => {
    return {
        title: 'The following email or password is incorrect',
        description: (
            <>
                Please{' '}
                <ResetPasswordLink
                    placement={PLACEMENT}
                    text="reset password"
                />{' '}
                or try again.
            </>
        ),
    }
}

const lockoutWarningMessage = (
    loginError: LoginErrorResponseType,
): LoginAlertDataType => {
    return {
        title: 'The following email or password is incorrect',
        description: (
            <>
                You have {loginError.loginRemainingAttempts} attempts remaining
                before your account will be locked.{' '}
                <ResetPasswordLink placement={PLACEMENT} />
            </>
        ),
    }
}

const finalLockoutWarningMessage = (): LoginAlertDataType => {
    return {
        title: 'The following email or password is incorrect',
        description: (
            <>
                You have one more attempt remaining before your account will be
                locked. You will be unable to reset your password while your
                account is locked. <ResetPasswordLink placement={PLACEMENT} />
            </>
        ),
    }
}

const lockedOutMessage = (
    loginError: LoginErrorResponseType,
): LoginAlertDataType => {
    return {
        title: 'Your account is temporarily locked',
        description: (
            <>
                You have exceeded the number of consecutive failed login
                attempts. Please wait{' '}
                <strong>{loginError.remainingMinsToUnlock} min</strong> and try
                again or{' '}
                <Link
                    name={`${PLACEMENT}-contact-us-link`}
                    data-test-target={`${PLACEMENT}-contact-us-link`}
                    placement={PLACEMENT}
                    href={mailtoLink(
                        HELP_EMAIL,
                        'I need help unlocking my account',
                    )}
                    target="_blank"
                >
                    contact us
                </Link>{' '}
                to unlock your account.
            </>
        ),
        icon: <LockFilled />,
    }
}

export type LoginErrorAlertProps = {
    loginError: LoginErrorType
}

const LoginErrorAlert = (props: LoginErrorAlertProps) => {
    const { loginError } = props

    const messages: LoginAlertMessageType = {
        [LoginErrorEnum.INCORRECT_EMAIL]: incorrectEmailMessage(),
        [LoginErrorEnum.LOCKOUT_WARNING]: lockoutWarningMessage(
            loginError.err as LoginErrorResponseType,
        ),
        [LoginErrorEnum.FINAL_LOCKOUT_WARNING]: finalLockoutWarningMessage(),
        [LoginErrorEnum.LOCKED_OUT]: lockedOutMessage(
            loginError.err as LoginErrorResponseType,
        ),
    }

    const message = messages[loginError.type]

    return (
        <Alert
            message={message.title}
            description={message.description}
            data-test-target={LoginErrorEnum[loginError.type]}
            type="error"
            showIcon
            icon={message.icon}
            style={{ marginBottom: '40px', width: '100%' }}
        />
    )
}

export default LoginErrorAlert
